// ClusterPeek — the cluster peek body, rendered by the global PeekHost from
// `?peek=clusters:<id>` (ADR-036 §2) — the sibling of ZonePeek: the parent ZONE
// rides the header eyebrow (`Cluster · <zone>`), then a Contents section (the
// read-only StatStrip) and — when the field-force module is on — a
// Responsibilities section. The acts come from the cluster's ONE registry
// (`useClusterActions`): the top row = Edit cluster (the primary — the one edit, a quiet
// button); the Responsibilities card carries its own act, "Manage" (a cluster is
// PAGELESS — ADR-051: its sectioned edit lives on its card, never in the Edit
// menu — ADR-036 §7, exception 1; a namer outside the cluster's own scope sees the
// link disabled, "Outside your scope" under it); the footer = the labelled
// Actions door (Set as default · the lifecycle). No "Open full page".

import { useTranslation } from 'react-i18next';
import type { ClusterDetail } from '@ems-app/schemas/clusters/responses';
import { useCluster } from '../../../hooks/useClusters.js';
import { useEntitlements } from '../../../hooks/useEntitlements.js';
import { ContextPanel } from '../../../components/patterns/ContextPanel.js';
import { DetailSection } from '../../../components/patterns/DetailSection.js';
import { SectionAct } from '../../../components/patterns/SectionAct.js';
import { StatStrip } from '../../../components/patterns/StatStrip.js';
import { ScopeChainsSection } from '../../../components/patterns/ScopeChainsSection.js';
import { useEntityPeek } from '../../../hooks/useEntityPeek.js';
import { Alert } from '../../../components/ui/Alert.js';
import { PageLoader } from '../../../components/ui/PageLoader.js';
import { icons } from '../../../components/ui/icons.js';
import { formatNumber } from '../../../lib/format.js';
import { ClusterStatusGroup } from './clusterDisplay.js';
import { useClusterActions } from './ClusterActions.js';

export interface ClusterPeekProps {
  projectId: string;
  clusterId: number;
  onClose: () => void;
}

export function ClusterPeek({ projectId, clusterId, onClose }: ClusterPeekProps) {
  const { t } = useTranslation('clusters');
  const clusterQ = useCluster(projectId, clusterId);

  if (clusterQ.isPending) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('list.title')}>
        <div className="flex min-h-[8rem] items-center justify-center">
          <PageLoader />
        </div>
      </ContextPanel>
    );
  }
  if (clusterQ.isError || !clusterQ.data) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('detail.error')}>
        <Alert variant="danger">{t('detail.error')}</Alert>
      </ContextPanel>
    );
  }
  return <ClusterPeekBody projectId={projectId} cluster={clusterQ.data} onClose={onClose} />;
}

// The loaded peek — its own component so the acts hook runs on a cluster that is there.
function ClusterPeekBody({ projectId, cluster: c, onClose }: { projectId: string; cluster: ClusterDetail; onClose: () => void }) {
  const { t } = useTranslation('clusters');
  const hasFieldForce = useEntitlements(projectId).has('has_field_force');
  const { acts, overlays } = useClusterActions(projectId, c, { onGone: onClose });
  // A person on a chain is followed inside the sheet — the cluster joins the way back.
  const { followPeek } = useEntityPeek();
  const peekPerson = (userId: string) => followPeek(`users:${userId}`, c.name);
  const stats = [
    { label: t('detail.contents.sites'), value: formatNumber(c.site_count) },
    { label: t('detail.contents.warehouses'), value: formatNumber(c.warehouse_count) },
    { label: t('detail.contents.vehiclePools'), value: formatNumber(c.vehicle_pool_count) },
  ];

  return (
    <>
      <ContextPanel
        host="peek"
        onClose={onClose}
        leading={<icons.cluster size={22} aria-hidden className="text-[var(--color-text-secondary)]" />}
        title={c.name}
        // The parent zone rides the eyebrow (a name in the numeric StatStrip read as a number).
        kind={`${t('detail.kind')} · ${c.zone_name}`}
        status={<ClusterStatusGroup isDisabled={c.is_disabled} isDefault={c.is_default} t={t} />}
        acts={acts}
        pageless
      >
        <DetailSection dense title={t('detail.sections.contents')}>
          {/* Unframed StatStrip — the section card IS the frame (DESIGN-TOKENS §StatStrip). */}
          <StatStrip stats={stats} />
        </DetailSection>
        {hasFieldForce && (
          <DetailSection dense title={t('detail.sections.responsibilities')} action={<SectionAct acts={acts} section="responsibilities" />}>
            <ScopeChainsSection projectId={projectId} scope={{ level: 'cluster', id: c.id }} peekPerson={peekPerson} />
          </DetailSection>
        )}
      </ContextPanel>
      {overlays}
    </>
  );
}
